import React from 'react';

// ─── SVG Filter (render once in the DOM) ──────────────────────────────────────
export const GlassFilter = () => (
  <svg style={{ display: 'none' }} aria-hidden="true">
    <defs>
      <filter
        id="glass-distortion"
        x="0%"
        y="0%"
        width="100%"
        height="100%"
        filterUnits="objectBoundingBox"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.001 0.005"
          numOctaves="1"
          seed="17"
          result="turbulence"
        />
        <feComponentTransfer in="turbulence" result="mapped">
          <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
          <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
          <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
        </feComponentTransfer>
        <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
        <feSpecularLighting
          in="softMap"
          surfaceScale="5"
          specularConstant="1"
          specularExponent="100"
          lightingColor="white"
          result="specLight"
        >
          <fePointLight x="-200" y="-200" z="300" />
        </feSpecularLighting>
        <feComposite
          in="specLight"
          operator="arithmetic"
          k1="0"
          k2="1"
          k3="1"
          k4="0"
          result="litImage"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="softMap"
          scale="200"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </defs>
  </svg>
);

// ─── Core Glass Wrapper ────────────────────────────────────────────────────────
/**
 * GlassEffect — wraps any content with a liquid-glass card appearance.
 *
 * @param {React.ReactNode}  children
 * @param {string}           className   - extra Tailwind classes for outer div
 * @param {React.CSSProperties} style    - extra inline styles
 * @param {string}           href        - if provided, wraps in <a>
 * @param {string}           target      - link target (default "_blank")
 */
export const GlassEffect = ({
  children,
  className = '',
  style = {},
  href,
  target = '_blank',
}) => {
  const glassStyle = {
    boxShadow: '0 6px 6px rgba(0,0,0,0.2), 0 0 20px rgba(0,0,0,0.1)',
    transitionTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 2.2)',
    ...style,
  };

  const content = (
    <div
      className={`relative flex font-semibold overflow-hidden cursor-pointer transition-all duration-700 ${className}`}
      style={glassStyle}
    >
      {/* Blur + distortion layer */}
      <div
        className="absolute inset-0 z-0 overflow-hidden rounded-[inherit]"
        style={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          filter: 'url(#glass-distortion)',
          isolation: 'isolate',
        }}
      />
      {/* Semi-transparent white fill */}
      <div
        className="absolute inset-0 z-10 rounded-[inherit]"
        style={{ background: 'rgba(255, 255, 255, 0.12)' }}
      />
      {/* Inner highlight borders */}
      <div
        className="absolute inset-0 z-20 rounded-[inherit] overflow-hidden"
        style={{
          boxShadow:
            'inset 2px 2px 1px 0 rgba(255,255,255,0.45), inset -1px -1px 1px 1px rgba(255,255,255,0.25)',
        }}
      />
      {/* Content */}
      <div className="relative z-30 w-full">{children}</div>
    </div>
  );

  return href ? (
    <a href={href} target={target} rel="noopener noreferrer" className="block">
      {content}
    </a>
  ) : (
    content
  );
};

// ─── Glass Button ──────────────────────────────────────────────────────────────
export const GlassButton = ({ children, href, className = '', onClick }) => (
  <GlassEffect
    href={href}
    className={`rounded-full px-8 py-3.5 hover:px-9 hover:py-4 overflow-hidden ${className}`}
    style={{ transitionTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 2.2)' }}
  >
    <div
      className="transition-all duration-700 text-white text-sm font-semibold uppercase tracking-widest"
      style={{ transitionTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 2.2)' }}
      onClick={onClick}
    >
      {children}
    </div>
  </GlassEffect>
);

// ─── Glass Card (main hero card) ──────────────────────────────────────────────
/**
 * GlassCard — a padded glass panel, ideal for the Hero section.
 */
export const GlassCard = ({ children, className = '' }) => (
  <GlassEffect
    className={`rounded-3xl p-10 md:p-14 w-full ${className}`}
  >
    {children}
  </GlassEffect>
);

export default GlassEffect;
